import Groq from "groq-sdk";

/**
 * GROQ AI Service - 100% FREE, SUPER FAST!
 * No credit card needed!
 * Get your free API key at: https://console.groq.com/keys
 */

// Initialize Groq client
const getGroqClient = () => {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;

  if (!apiKey) {
    console.warn('⚠️ Groq API key not found. Please add REACT_APP_GROQ_API_KEY to your .env file');
    return null;
  }

  return new Groq({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // For client-side usage
  });
};

// System prompt for business assistant
const SYSTEM_PROMPT = `You are an intelligent business assistant for a salon management system. Your role is to help users query and analyze their business data.

**Available Data Entities (from your system):**
- user: User/Customer information
- product: Products and inventory items
- services: Salon services (parent and child services)
- booking: Appointments and bookings
- staff: Staff/Employee management
- package: Service packages/bundles
- membership: Customer memberships and subscriptions
- client: Client information
- bill: Billing and invoices
- inventory: Stock and inventory management
- vendor: Vendor information
- order: Purchase orders
- expense: Business expenses
- attendance: Staff attendance
- closing: Daily closing reports
- survey: Customer surveys
- task: Task management
- coupon: Discount coupons
- wallet: Wallet transactions
- seat: Seat/station management
- advertisement: Promotional content
- media: Media files

**Your Task:**
Extract structured information from user queries and respond in JSON format ONLY.

**Response Format (JSON ONLY):**
{
  "intent": "fetch_data" | "analytics" | "conversation" | "dashboard",
  "entity": "user" | "product" | "services" | "booking" | "staff" | "package" | "membership" | "client" | "bill" | "inventory" | "vendor" | "order" | "expense" | "attendance" | "closing" | "survey" | "task" | "coupon" | "wallet" | "seat" | "advertisement" | "media" | "dashboard" | null,
  "limit": number | null,
  "filters": {
    "status": "active" | "inactive" | null,
    "minPrice": number | null,
    "maxPrice": number | null,
    "date": "today" | "yesterday" | "YYYY-MM-DD" | null,
    "dateRange": "week" | "month" | null,
    "specificDate": "YYYY-MM-DD" | null,
    "customDateRange": {"start": "YYYY-MM-DD", "end": "YYYY-MM-DD"} | null
  },
  "sorting": {
    "field": "price" | "createdAt" | "name" | null,
    "direction": "asc" | "desc" | null
  },
  "message": "friendly message explaining what you'll fetch"
}

**Examples:**

User: "Show me 5 products"
Response: {"intent":"fetch_data","entity":"product","limit":5,"filters":{},"sorting":{},"message":"I'll show you 5 products from your inventory."}

User: "Find active users"
Response: {"intent":"fetch_data","entity":"user","limit":null,"filters":{"status":"active"},"sorting":{},"message":"I'll find all active users in your system."}

User: "Give me 3 highest priced products"
Response: {"intent":"fetch_data","entity":"product","limit":3,"filters":{},"sorting":{"field":"price","direction":"desc"},"message":"I'll show you the 3 most expensive products."}

User: "Show bills from today"
Response: {"intent":"fetch_data","entity":"bill","limit":null,"filters":{"date":"today"},"sorting":{},"message":"I'll show you all bills generated today."}

User: "Show bookings this week"
Response: {"intent":"fetch_data","entity":"booking","limit":null,"filters":{"dateRange":"week"},"sorting":{},"message":"I'll show you all bookings from this week."}

User: "Get yesterday's expenses"
Response: {"intent":"fetch_data","entity":"expense","limit":null,"filters":{"date":"yesterday"},"sorting":{},"message":"I'll fetch yesterday's expenses."}

User: "Show this month's sales"
Response: {"intent":"fetch_data","entity":"bill","limit":null,"filters":{"dateRange":"month"},"sorting":{},"message":"I'll show you all bills from this month."}

User: "Business dashboard"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{},"sorting":{},"message":"I'll show you the complete business analytics dashboard."}

User: "Show me dashboard"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{},"sorting":{},"message":"I'll fetch the business dashboard with all key metrics."}

User: "Dashboard for today"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{"date":"today"},"sorting":{},"message":"I'll show you today's dashboard."}

User: "Dashboard for this week"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{"dateRange":"week"},"sorting":{},"message":"I'll show you this week's dashboard."}

User: "Show dashboard for 15 November"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{"specificDate":"2025-11-15"},"sorting":{},"message":"I'll show you the dashboard for 15 November 2025."}

User: "Show dashboard for 15 November 2025"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{"specificDate":"2025-11-15"},"sorting":{},"message":"I'll show you the dashboard for 15 November 2025."}

User: "Get bills from 10th November"
Response: {"intent":"fetch_data","entity":"bill","limit":null,"filters":{"specificDate":"2025-11-10"},"sorting":{},"message":"I'll fetch bills from 10th November 2025."}

User: "Show bookings on 2025-11-20"
Response: {"intent":"fetch_data","entity":"booking","limit":null,"filters":{"specificDate":"2025-11-20"},"sorting":{},"message":"I'll show bookings for 20th November 2025."}

User: "Show dashboard from 12 Nov to 16 Nov"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{"customDateRange":{"start":"2025-11-12","end":"2025-11-16"}},"sorting":{},"message":"I'll show you the dashboard from 12th November to 16th November 2025."}

User: "Get bills from 10 November to 15 November"
Response: {"intent":"fetch_data","entity":"bill","limit":null,"filters":{"customDateRange":{"start":"2025-11-10","end":"2025-11-15"}},"sorting":{},"message":"I'll fetch bills from 10th November to 15th November 2025."}

User: "Show bookings between 5 Nov and 10 Nov"
Response: {"intent":"fetch_data","entity":"booking","limit":null,"filters":{"customDateRange":{"start":"2025-11-05","end":"2025-11-10"}},"sorting":{},"message":"I'll show bookings from 5th November to 10th November 2025."}

User: "Dashboard from 1 December to 15 December"
Response: {"intent":"dashboard","entity":"dashboard","limit":null,"filters":{"customDateRange":{"start":"2025-12-01","end":"2025-12-15"}},"sorting":{},"message":"I'll show you the dashboard from 1st December to 15th December 2025."}

User: "Show latest 10 bookings"
Response: {"intent":"fetch_data","entity":"booking","limit":10,"filters":{},"sorting":{"field":"createdAt","direction":"desc"},"message":"I'll show you the latest 10 bookings."}

**IMPORTANT:**
- Always respond with ONLY valid JSON
- Do not add any text before or after the JSON
- Be accurate with entity extraction`;

/**
 * Process user query with Groq AI (FREE & SUPER FAST!)
 */
export const processQueryWithGroqAI = async (userMessage, conversationHistory = []) => {
  try {
    const client = getGroqClient();

    if (!client) {
      return {
        success: false,
        error: 'Groq API key not configured. Please add REACT_APP_GROQ_API_KEY to your .env file',
        needsConfig: true
      };
    }

    console.log('🚀 Processing with Groq AI (FREE & FAST!):', userMessage);

    // Build conversation with context
    const messages = [
      {
        role: "system",
        content: SYSTEM_PROMPT
      },
      // Include last 3 messages for context
      ...conversationHistory.slice(-3).map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      })),
      {
        role: "user",
        content: userMessage
      }
    ];

    // Call Groq API with Llama 3 (fastest free model)
    const chatCompletion = await client.chat.completions.create({
      messages: messages,
      model: "llama-3.3-70b-versatile", // Super fast and accurate!
      temperature: 0.7,
      max_tokens: 500,
      top_p: 1,
      stream: false,
      response_format: { type: "json_object" } // Ensures JSON response
    });

    const aiResponse = chatCompletion.choices[0]?.message?.content || '{}';
    console.log('✅ Groq AI Response:', aiResponse);

    // Parse JSON response
    const parsedData = JSON.parse(aiResponse);

    return {
      success: true,
      data: parsedData,
      source: 'groq_ai',
      model: 'llama-3.3-70b-versatile',
      usage: {
        prompt_tokens: chatCompletion.usage?.prompt_tokens || 0,
        completion_tokens: chatCompletion.usage?.completion_tokens || 0,
        total_tokens: chatCompletion.usage?.total_tokens || 0
      }
    };

  } catch (error) {
    console.error('❌ Groq AI Error:', error);

    return {
      success: false,
      error: error.message || 'Failed to process query with Groq AI',
      details: error
    };
  }
};

/**
 * Generate analytics summary with Groq AI
 */
export const generateGroqAnalyticsSummary = async (businessData) => {
  try {
    const client = getGroqClient();

    if (!client) {
      return generateSimpleSummary(businessData);
    }

    const prompt = `Analyze this business data and provide a concise, actionable summary:

**Business Data:**
- Users/Customers: ${businessData.users?.count || 0}
- Products in Inventory: ${businessData.products?.count || 0}
- Total Bookings: ${businessData.bookings?.count || 0}
- Staff Members: ${businessData.staff?.count || 0}

Provide a brief 2-3 sentence summary with:
1. Key observation
2. One actionable insight

Keep it under 100 words and make it helpful for a salon business owner.`;

    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a business analyst providing concise insights for salon management."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      model: "llama-3.3-70b-versatile",
      temperature: 0.8,
      max_tokens: 200,
    });

    return completion.choices[0]?.message?.content || generateSimpleSummary(businessData);

  } catch (error) {
    console.error('Error generating analytics with Groq:', error);
    return generateSimpleSummary(businessData);
  }
};

/**
 * Simple fallback summary (no AI needed)
 */
const generateSimpleSummary = (businessData) => {
  const { users, products, bookings, staff } = businessData;

  let summary = '**Quick Overview:**\n\n';

  if (users?.count > 0) {
    summary += `• ${users.count} customers in database\n`;
  }

  if (products?.count > 0) {
    summary += `• ${products.count} products in inventory\n`;
  }

  if (bookings?.count > 0) {
    summary += `• ${bookings.count} total bookings\n`;
  }

  if (staff?.count > 0) {
    summary += `• ${staff.count} team members\n`;
  }

  summary += '\n💡 Your business data is being tracked successfully!';

  return summary;
};

/**
 * Check if Groq AI is configured
 */
export const isGroqConfigured = () => {
  return !!process.env.REACT_APP_GROQ_API_KEY;
};

/**
 * Get Groq configuration instructions
 */
export const getGroqSetupInstructions = () => {
  return {
    title: 'Get FREE Groq API Key',
    steps: [
      'Go to https://console.groq.com',
      'Sign up (no credit card required!)',
      'Go to https://console.groq.com/keys',
      'Click "Create API Key"',
      'Copy your key',
      'Add to .env: REACT_APP_GROQ_API_KEY=your-key-here',
      'Restart your app'
    ],
    benefits: [
      '100% FREE forever',
      'Super fast responses (fastest in the market)',
      'No credit card needed',
      'Generous free tier',
      'Real LLM AI (Llama 3.3)',
      'Better than GPT-3.5 for many tasks'
    ]
  };
};

/**
 * Analyze dashboard query with Groq AI
 */
export const analyzeDashboardQuery = async (dashboardData, queryType, userQuery, limit = null) => {
  try {
    const client = getGroqClient();

    if (!client) {
      return generateSimpleDashboardAnswer(dashboardData, queryType);
    }

    console.log('🤖 Analyzing dashboard with Groq AI:', queryType);

    // Prepare dashboard data summary for AI
    const dataSummary = JSON.stringify(dashboardData, null, 2);

    const prompt = `Analyze this dashboard data and answer the user's question.

**User Question:** ${userQuery}
**Query Type:** ${queryType || 'general'}
${limit ? `**Limit:** Show top ${limit} items` : ''}

**Dashboard Data:**
${dataSummary}

Provide a clear, concise answer based on the data. Include:
1. Direct answer to the question
2. Key insights or patterns
3. Relevant numbers/statistics

Keep it professional and business-focused. Format with markdown for readability.`;

    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a business analyst helping analyze salon/spa dashboard data. Provide clear, actionable insights."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      model: "llama-3.3-70b-versatile",
      temperature: 0.7,
      max_tokens: 500,
    });

    return completion.choices[0]?.message?.content || generateSimpleDashboardAnswer(dashboardData, queryType);

  } catch (error) {
    console.error('Error analyzing dashboard with Groq:', error);
    return generateSimpleDashboardAnswer(dashboardData, queryType);
  }
};

/**
 * Simple fallback dashboard answer (no AI needed)
 */
const generateSimpleDashboardAnswer = (dashboardData, queryType) => {
  let answer = '📊 **Dashboard Analysis:**\n\n';

  if (dashboardData) {
    answer += `Here's a summary of your dashboard data:\n\n`;

    // Add basic data points
    const dataKeys = Object.keys(dashboardData);
    dataKeys.slice(0, 5).forEach(key => {
      const value = dashboardData[key];
      if (typeof value === 'number') {
        answer += `• ${key}: ${value}\n`;
      } else if (typeof value === 'string') {
        answer += `• ${key}: ${value}\n`;
      }
    });

    answer += `\n💡 For more detailed AI-powered insights, configure Groq API.`;
  } else {
    answer += `No dashboard data available for the specified criteria.`;
  }

  return answer;
};

export default {
  processQueryWithGroqAI,
  generateGroqAnalyticsSummary,
  isGroqConfigured,
  getGroqSetupInstructions,
  analyzeDashboardQuery
};
